import React, { useState, useEffect } from "react";
import Pagination from "@mui/material/Pagination";
import { ColorRing } from "react-loader-spinner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import API_BASE_URL from "../../../../Constants";
import ListHeader from "../../../../components/ListHeader/NoCardView/index";
import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";
import ViewInspectionDeviceModal from "../InspectionList/ViewInspectionDeviceModal";
import InspectionWizardModal from "../InspectionList/InspectionWizard/InspectionWizardModal";

const InspectionListFields = [
  { key: "actions", label: "Actions" },
  { key: "inspectionNumber", label: "Inspection #" },
  { key: "buildingReference", label: "Building" },
  { key: "managementCompanyName", label: "Mgmt Co" },
  { key: "agencyName", label: "Agency" },
  { key: "test", label: "Test Label" },
  { key: "approvalDate", label: "Approval Date" },
  { key: "lastDate", label: "Last Date" },
  { key: "nextDate", label: "Next Date" },
  { key: "hours", label: "Hours" },
  { key: "taskType", label: "Task Type" },
  { key: "inspYear", label: "Inspection Year" },
  { key: "status", label: "Status" },
];

export default function InspectionsListWithAgencyTestFilters() {
  // Agency & Tests
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState("");

  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState("");

  // Inspections
  const [inspections, setInspections] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Modal & Wizard
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [selectedInspectionNumber, setSelectedInspectionNumber] =
    useState(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Table vs. card
  const [viewMode, setViewMode] = useState("table");

  // A) Fetch agencies on mount
  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        setIsLoading(true);
        const res = await fetch(
          `${API_BASE_URL}/api/inspections/agency/agencies`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) {
          throw new Error(`Failed to fetch agencies. Status: ${res.status}`);
        }
        const data = await res.json();
        setAgencies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching agencies:", err);
        setAgencies([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAgencies();
  }, []);

  // B) Whenever selectedAgencyId changes => fetch tests
  useEffect(() => {
    if (!selectedAgencyId) {
      setTests([]);
      setSelectedTestId("");
      return;
    }

    const fetchTests = async () => {
      try {
        setIsLoading(true);
        const url = `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${selectedAgencyId}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          throw new Error(`Failed to fetch tests. Status: ${res.status}`);
        }
        const data = await res.json();
        setTests(Array.isArray(data) ? data : []);
        setSelectedTestId("");
      } catch (err) {
        console.error("Error fetching tests:", err);
        setTests([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTests();
  }, [selectedAgencyId]);

  // C) Fetch inspections on filter/pagination changes
  useEffect(() => {
    fetchInspections();
    // eslint-disable-next-line
  }, [selectedAgencyId, selectedTestId, currentPage, rowsPerPage]);

  const fetchInspections = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (selectedAgencyId) params.append("agencyId", selectedAgencyId);
      if (selectedTestId) params.append("testId", selectedTestId);
      if (searchQuery) params.append("q", searchQuery);
      params.append("page", currentPage - 1);
      params.append("size", rowsPerPage);

      const url = `${API_BASE_URL}/api/inspections/with-joins-listdto?${params}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setInspections(data.content || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching inspections:", err);
      setInspections([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshList = async () => {
    setCurrentPage(1);
    await fetchInspections();
  };

  // D) Searching
  const handleSearch = () => {
    setCurrentPage(1);
    fetchInspections();
  };
  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  // E) Excel export
  const exportData = (dataToExport, filename) => {
    if (!dataToExport || !dataToExport.length) {
      alert("No data to export!");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
  };

  // F) “View” => open modal
  const handleViewClick = (inspectionNumber) => {
    const found = inspections.find(
      (i) => i.inspectionNumber === inspectionNumber
    );
    if (found) {
      setSelectedInspection(found);
      setSelectedInspectionNumber(found.inspectionNumber);
      setIsModalOpen(true);
    }
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInspection(null);
    setSelectedInspectionNumber(null);
  };

  // G) Pagination
  const handlePageChange = (event, page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // H) “Add Inspection” => open wizard
  const handleOpenCreateModal = () => setIsWizardOpen(true);
  const handleCloseWizard = () => setIsWizardOpen(false);

  // I) Render
  return (
    <div className="w-full h-screen flex flex-col">
      {/* ListHeader at the top */}
      <ListHeader
        title="Inspection Tests Due"
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
        handleSearch={handleSearch}
        viewMode={viewMode}
        handleViewModeChange={setViewMode}
        handleOpenCreateModal={handleOpenCreateModal}
      >
        {/* Put all your filters in a single flex row so they don’t wrap */}
        <div className="flex items-center space-x-2 whitespace-nowrap">
          {/* Agency Filter */}
          <label className="text-sm font-semibold">Agency</label>
          <button
            type="button"
            className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                       text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                       hover:text-white transition flex items-center"
          >
            <select
              value={selectedAgencyId}
              onChange={(e) => setSelectedAgencyId(e.target.value)}
              className="bg-transparent border-none outline-none cursor-pointer"
            >
              <option value="">All</option>
              {agencies.map((agency) => (
                <option key={agency.agencyId} value={agency.agencyId}>
                  {agency.agencyName || `Agency #${agency.agencyId}`}
                </option>
              ))}
            </select>
          </button>

          {/* Test Filter */}
          <label className="text-sm font-semibold">Test</label>
          <button
            type="button"
            className={`px-4 py-2 border-2 border-sss-blue-sel rounded-lg 
                       text-sss-blue-sel font-bold hover:bg-sss-blue-sel 
                       hover:text-white transition flex items-center
                       ${
                         !selectedAgencyId
                           ? "cursor-not-allowed opacity-70"
                           : ""
                       }`}
            disabled={!selectedAgencyId}
          >
            <select
              value={selectedTestId}
              onChange={(e) => setSelectedTestId(e.target.value)}
              className="bg-transparent border-none outline-none cursor-pointer"
            >
              <option value="">All</option>
              {tests.map((test) => (
                <option key={test.testId} value={test.testId}>
                  {test.label ?? `Test #${test.testId}`}
                </option>
              ))}
            </select>
          </button>

          {/* Excel Button */}
          <button
            type="button"
            onClick={() => exportData(inspections, "AllInspectionsList.xlsx")}
            className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg 
                       text-sss-blue-sel font-bold hover:bg-sss-blue-sel 
                       hover:text-white transition"
          >
            Excel
          </button>
        </div>
      </ListHeader>

      <hr className="border-gray-200 mb-2" />

      {/* Table or card container */}
      <div className="flex-1 overflow-x-auto">
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <ColorRing
              visible
              height="200"
              width="200"
              ariaLabel="color-ring-loading"
              colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
            />
          </div>
        )}

        {!isLoading && (
          <>
            {viewMode === "table" && (
              <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
                <thead className="bg-gray-200">
                  <tr>
                    {InspectionListFields.map((field) => (
                      <th
                        key={field.key}
                        className="px-4 py-2 text-left font-bold text-sss-blue-sel"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {inspections.length > 0 ? (
                    inspections.map((insp, idx) => (
                      <tr
                        key={idx}
                        className="border-t hover:bg-gray-50 transition"
                      >
                        {InspectionListFields.map((col) => {
                          if (col.key === "actions") {
                            return (
                              <td
                                key={col.key}
                                className="px-4 py-2 whitespace-nowrap"
                              >
                                <button
                                  className="text-sss-info font-semibold hover:underline"
                                  onClick={() =>
                                    handleViewClick(insp.inspectionNumber)
                                  }
                                >
                                  View
                                </button>
                              </td>
                            );
                          }
                          return (
                            <td
                              key={col.key}
                              className="px-4 py-2 whitespace-nowrap"
                            >
                              {insp[col.key] ?? "N/A"}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={InspectionListFields.length}
                        className="text-center py-4"
                      >
                        No inspections found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {viewMode === "card" && (
              <div className="m-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {inspections.length > 0 ? (
                  inspections.map((insp, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200 flex flex-col"
                    >
                      <h3 className="text-md font-bold mb-2">
                        Inspection #{insp.inspectionNumber ?? "N/A"}
                      </h3>
                      <p>Building: {insp.buildingReference ?? "N/A"}</p>
                      <p>Mgmt Co: {insp.managementCompanyName ?? "N/A"}</p>
                      <p>Agency: {insp.agencyName ?? "N/A"}</p>
                      <p>Test Label: {insp.test ?? "N/A"}</p>
                      <p>Task Type: {insp.taskType ?? "N/A"}</p>
                      <p>Status: {insp.status ?? "N/A"}</p>

                      <button
                        className="mt-2 text-sss-info font-semibold hover:underline self-start"
                        onClick={() => handleViewClick(insp.inspectionNumber)}
                      >
                        View
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="col-span-full text-center py-4">
                    No inspections found.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination at bottom */}
      <div className="border-t border-gray-300 bg-white p-4 flex justify-between items-center">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />
        <RowsPerPageSelector
          rowsPerPage={rowsPerPage}
          setRowsPerPage={handleRowsPerPageChange}
        />
      </div>

      {/* “View” Modal */}
      {isModalOpen && selectedInspectionNumber && (
        <ViewInspectionDeviceModal
          open={isModalOpen}
          inspectionNumber={selectedInspectionNumber}
          selectedInspection={selectedInspection}
          onClose={handleCloseModal}
          refreshList={refreshList}
        />
      )}

      {/* “Add” Wizard */}
      {isWizardOpen && (
        <InspectionWizardModal
          onClose={handleCloseWizard}
          refreshList={refreshList}
        />
      )}
    </div>
  );
}
